import { paymentStatementApi } from './base';

export type PartnerType =
	| 'bank'
	| 'pos_dealer'
	| 'cpa_firm'
	| 'software'
	| 'direct';

export const PARTNER_TYPE_LABELS: Record<PartnerType, string> = {
	bank: 'Bank',
	pos_dealer: 'POS dealer',
	cpa_firm: 'CPA firm',
	software: 'Software',
	direct: 'Direct',
};

export interface PartnerFields {
	name: string;
	partner_type: PartnerType;
	name_on_proposals: string | null;
	logo_data_url: string | null;
	show_logo: boolean;
	show_name: boolean;
	contact_name: string | null;
	contact_email: string | null;
	contact_phone: string | null;
	website: string | null;
	address: string | null;
	revenue_config_name: string | null;
	statement_mailbox: string | null;
}

export interface Partner extends PartnerFields {
	id: string;
	template_count: number;
	created_at: string;
	updated_at: string;
}

interface PartnerListResponse {
	success: boolean;
	data: Partner[];
}

interface PartnerResponse {
	success: boolean;
	data: Partner;
}

export const partnersApi = paymentStatementApi.injectEndpoints({
	endpoints: (builder) => ({
		listPartners: builder.query<Partner[], { type?: PartnerType } | void>({
			query: (params) => ({
				url: '/partners',
				params: params?.type ? { type: params.type } : {},
			}),
			transformResponse: (response: PartnerListResponse) => response.data,
			providesTags: ['Partners'],
		}),
		getPartner: builder.query<Partner, string>({
			query: (id) => `/partners/${id}`,
			transformResponse: (response: PartnerResponse) => response.data,
			providesTags: (_result, _error, id) => [{ type: 'Partners', id }],
		}),
		createPartner: builder.mutation<Partner, PartnerFields>({
			query: (body) => ({ url: '/partners', method: 'POST', body }),
			transformResponse: (response: PartnerResponse) => response.data,
			invalidatesTags: ['Partners'],
		}),
		updatePartner: builder.mutation<
			Partner,
			{ id: string; body: Partial<PartnerFields> }
		>({
			query: ({ id, body }) => ({
				url: `/partners/${id}`,
				method: 'PUT',
				body,
			}),
			transformResponse: (response: PartnerResponse) => response.data,
			invalidatesTags: (_result, _error, { id }) => [
				'Partners',
				{ type: 'Partners', id },
			],
		}),
	}),
});

export const {
	useListPartnersQuery,
	useGetPartnerQuery,
	useCreatePartnerMutation,
	useUpdatePartnerMutation,
} = partnersApi;
